const rand = (i, seed) => { const x = Math.sin(i * 12.9898 + seed * 78.233) * 43758.5453; return x - Math.floor(x); };

const LEAF = "M12 2 L13.5 7 L17 5.5 L16 9.5 L21 9 L18 12.5 L20.5 14 L15 15 L15.5 18 L12.8 16.5 L12.5 22 L11.5 22 L11.2 16.5 L8.5 18 L9 15 L3.5 14 L6 12.5 L3 9 L8 9.5 L7 5.5 L10.5 7 Z";
const COLORS = {
  leaves: ["#E8892B", "#C9471E", "#F2B33D", "#9E2F17", "#D96A1F"],
  petals: ["#FFD1DC", "#FFB3C7", "#FFFFFF", "#FFC2D1"],
  poppies: ["#C8102E", "#E0303F", "#B30E26"],
  confetti: ["#F2C45C", "#FFFFFF", "#C8102E", "#3C5BBF", "#FFE08A"],
};

const Shape = ({ type, color }) => {
  if (type === "snow") return <i className="sp-flake" />;
  if (type === "fireflies") return <i className="sp-firefly" />;
  if (type === "confetti") return <i className="sp-confetti" style={{ color }} />;
  if (type === "leaves") return <svg viewBox="0 0 24 24"><path d={LEAF} fill={color} /><path d="M12 22 V8" stroke="rgba(0,0,0,0.25)" strokeWidth="0.8" /></svg>;
  if (type === "petals") return <svg viewBox="0 0 24 24"><path d="M12 2 C19 7 19 17 12 22 C5 17 5 7 12 2Z" fill={color} /><path d="M12 5 C15 9 15 15 12 19" stroke="rgba(255,255,255,0.6)" strokeWidth="0.8" fill="none" /></svg>;
  return <svg viewBox="0 0 24 24">{[0, 90, 180, 270].map((r) => <ellipse key={r} cx="12" cy="6.5" rx="5" ry="6" fill={color} transform={`rotate(${r} 12 12)`} />)}<circle cx="12" cy="12" r="2.6" fill="#1a1a1a" /></svg>;
};

/** Seasonal particles in two depth layers: small soft ones behind the logo, big blurred ones in front. */
export const SplashParticles = ({ theme, layer }) => {
  if (!theme.particles) return null;
  const front = layer === "front";
  const type = theme.particles;
  const count = front ? (type === "snow" ? 14 : 9) : (type === "snow" ? 60 : 30);
  const palette = COLORS[type] || ["#fff"];
  return (
    <div className={`splash-particles splash-particles-${type} ${front ? "is-front" : "is-back"}`} aria-hidden="true" data-testid={front ? undefined : `splash-atmosphere-${theme.id}`}>
      {Array.from({ length: count }, (_, i) => {
        const size = front ? 2.2 + rand(i, 1) * 2.6 : (type === "snow" ? 0.35 : 0.9) + rand(i, 1) * (type === "snow" ? 0.9 : 1.6);
        const fall = (front ? 3.6 : 7) + rand(i, 2) * (front ? 2.5 : 6);
        return (
          <span key={i} className="sp-p" style={{ left: `${rand(i, 3) * 100}%`, "--s": `${size}cqw`, animationDuration: `${fall}s`, animationDelay: `${-rand(i, 4) * fall}s` }}>
            <span className="sp-sway" style={{ animationDuration: `${2.2 + rand(i, 5) * 2.8}s`, animationDelay: `${-rand(i, 6) * 3}s` }}>
              <span className="sp-shape" style={{ animationDuration: `${2 + rand(i, 7) * 3}s` }}><Shape type={type} color={palette[i % palette.length]} /></span>
            </span>
          </span>
        );
      })}
    </div>
  );
};

const BULB_COLORS = ["#FF4D4D", "#FFD25C", "#3FD07A", "#5CA8FF", "#FF8BD1"];

/** Twinkling Christmas light strings swagged across the top of the stage. */
export const SplashLights = () => (
  <svg className="splash-lights" viewBox="0 0 1000 80" aria-hidden="true" data-testid="splash-christmas-lights">
    <defs><filter id="sp-bulb-glow" x="-200%" y="-200%" width="500%" height="500%"><feGaussianBlur stdDeviation="5" /></filter></defs>
    <path d="M0 6 Q166.5 106 333 6 Q499.5 106 666 6 Q832.5 106 1000 6" stroke="#0c1a12" strokeWidth="2.2" fill="none" />
    {Array.from({ length: 27 }, (_, i) => {
      const seg = Math.floor(i / 9); const t = (i % 9 + 0.5) / 9;
      const x = seg * 333 + t * 333; const y = 6 + 100 * t * (1 - t) + 4;
      const color = BULB_COLORS[i % BULB_COLORS.length];
      return (
        <g key={i} className="splash-bulb" style={{ animationDelay: `${(i % 5) * 0.32}s` }}>
          <circle cx={x} cy={y + 6} r="9" fill={color} filter="url(#sp-bulb-glow)" opacity="0.9" />
          <rect x={x - 2.4} y={y - 3} width="4.8" height="4" rx="1" fill="#2b2b2b" />
          <ellipse cx={x} cy={y + 6} rx="3.6" ry="6" fill={color} />
          <ellipse cx={x - 1.2} cy={y + 3.5} rx="1" ry="2" fill="#fff" opacity="0.7" />
        </g>
      );
    })}
  </svg>
);

const BURSTS = [
  { x: 14, y: 30, c: "#F2C45C", d: 0.4 }, { x: 84, y: 24, c: "#E86A75", d: 1.1 }, { x: 30, y: 72, c: "#65C4E3", d: 1.8 },
  { x: 72, y: 70, c: "#FFFFFF", d: 0.9 }, { x: 52, y: 14, c: "#F2C45C", d: 2.3 }, { x: 92, y: 58, c: "#F2C45C", d: 1.5 },
];

/** Looping firework bursts for the New Year theme. */
export const SplashFireworks = () => (
  <div className="splash-fireworks" aria-hidden="true" data-testid="splash-fireworks">
    {BURSTS.map((b, i) => (
      <span key={i} className="sp-burst" style={{ left: `${b.x}%`, top: `${b.y}%`, color: b.c, animationDelay: `${b.d}s` }}>
        {Array.from({ length: 14 }, (_, r) => <i key={r} style={{ transform: `rotate(${r * (360 / 14)}deg)` }} />)}
      </span>
    ))}
  </div>
);

const JetSvg = () => (
  <svg viewBox="0 0 200 60" className="splash-jet-svg">
    <circle cx="6" cy="30" r="7" fill="#FFB347" opacity="0.85" className="splash-burner" />
    <path d="M196 30 L150 24 L120 22 L96 7 L84 7 L91 22 L52 23 L36 9 L25 9 L29 25 L8 27 L4 30 L8 33 L29 35 L25 51 L36 51 L52 37 L91 38 L84 53 L96 53 L120 38 L150 36 Z" fill="#D9E1EA" />
    <path d="M120 22 L96 7 L84 7 L91 22 Z M36 9 L25 9 L29 25 L52 23 Z" fill="#AFBBC8" />
    <ellipse cx="152" cy="27" rx="15" ry="4.2" fill="#1B2D55" />
    <circle cx="70" cy="30" r="3.2" fill="#3C3B6E" /><circle cx="70" cy="30" r="1.4" fill="#fff" />
  </svg>
);

/** Signature jet flyover; the Veterans theme flies a three-ship formation. */
export const SplashJets = ({ theme }) => {
  const jets = theme.formation ? [{ y: 0, d: 0, s: 1 }, { y: -8, d: 0.16, s: 0.7 }, { y: 8, d: 0.24, s: 0.7 }] : [{ y: 0, d: 0, s: 1 }];
  return jets.map((jet, i) => (
    <div key={i} className="splash-jet" aria-hidden="true" data-testid={`splash-jet-${i}`} style={{ top: `calc(48% + ${jet.y}cqh)`, animationDelay: `${1.55 + jet.d}s`, "--jet-scale": jet.s }}>
      <div className="splash-trail">{theme.trail.map((color, k) => <span key={k} style={{ background: color }} />)}</div>
      <JetSvg />
    </div>
  ));
};
